<template>
  <v-dialog v-model="dialog" scrollable width="820">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        small
        color="secondary"
        depressed
        dark
        class="text-capitalize"
      >
        <v-icon left size="18">{{ icons.mdiEyeOutline }}</v-icon>
        İncele
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="grey">
        <div
          class="d-flex align-center justify-space-between"
          style="width: 100%"
        >
          <div class="d-flex align-center">
            <v-icon color="white" size="22">
              {{ icons.mdiFileDocumentOutline }}
            </v-icon>

            <div class="text-subtitle-1 font-weight-bold ms-3 white--text">
              {{ pending.mutabakat_no }} Mutabakat
            </div>
          </div>
          <v-btn color="grey" depressed fab small @click="closeForm()">
            <v-icon>
              {{ icons.mdiCloseCircleOutline }}
            </v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />

      <v-alert v-if="pending.passed_days != null" class="ma-0 py-2" tile dense :type="pending.must_approve ? 'error' : 'warning'" text>
        <div class="font-weight-bold">
          Mutabakat gönderiminin üzerinden {{ pending.passed_days }} gün geçti.
        </div>
        <div v-if="pending.must_approve" class="text-caption">
          Onaylama süresi geçmiştir.
        </div>
        <div v-else-if="pending.remaining_days != null" class="text-caption">
          Onaylamak için son {{ pending.remaining_days }} gününüz kaldı.
        </div>
      </v-alert>
      <v-alert v-else-if="pending.status_text" class="ma-0 py-2" tile dense :type="pending.status == 4 ? 'error' : 'success'" text>
        <div class="font-weight-bold">
          Mevcut durum: {{ pending.status_text }}
        </div>
        <div v-if="pending.note" class="text-caption">
          {{ pending.note }}
        </div>
      </v-alert>

      <v-card-text class="pa-0">
        <div v-if="pdfLoading" class="d-flex justify-center align-center" style="height: 480px">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else-if="pdfError" class="pa-6 text-center">
          {{ pdfError }}
        </div>
        <div v-else-if="pdfUrl" class="d-flex justify-center" style="max-height: 70svh; overflow: auto">
          <img
            v-if="isImageUrl(pdfUrl)"
            :src="pdfUrl"
            style="max-width: 100%; display: block"
          />
          <embed
            v-else
            :src="`${pdfUrl}#toolbar=0&navpanes=0&statusbar=0&view=FitH`"
            type="application/pdf"
            style="width: 100%; height: 70svh"
          />
        </div>
      </v-card-text>

      <template v-if="!isAnswered">
        <v-divider />

        <!-- Onay/Ret Formu -->
        <v-expand-transition>
          <div v-if="responseAction">
            <v-card-text class="pb-0">
              <div class="text-subtitle-2 font-weight-bold mb-3">
                {{ responseAction === "RET" ? "Mutabakatı Reddet" : "Mutabakatı Onayla" }}
              </div>
              <v-textarea
                v-model="note"
                outlined
                dense
                rows="3"
                counter="255"
                maxlength="255"
                hide-details="auto"
                :label="responseAction === 'RET' ? 'Ret nedeninizi kısaca açıklayınız' : 'Açıklama (opsiyonel)'"
                :rules="responseAction === 'RET' ? [(v) => !!(v && v.trim()) || 'Ret nedeninizi yazınız']  : []"
              />
              <v-file-input
                v-model="file"
                outlined
                dense
                class="mt-3"
                hide-details="auto"
                label="Dosya Ekle (opsiyonel)"
                accept="image/*,application/pdf"
                :prepend-icon="null"
                :prepend-inner-icon="icons.mdiPaperclip"
                :hint="'Yalnızca görsel veya PDF dosyası ekleyebilirsiniz'"
                persistent-hint
                show-size
              />
            </v-card-text>
          </div>
        </v-expand-transition>

        <v-card-actions class="pa-4">
          <template v-if="responseAction">
            <v-btn text class="text-capitalize" :disabled="approvalLoading" @click="cancelResponse">
              Vazgeç
            </v-btn>
            <v-spacer />
            <v-btn
              :color="responseAction === 'RET' ? 'error' : 'success'"
              depressed
              class="text-capitalize"
              :loading="approvalLoading"
              :disabled="approvalLoading"
              @click="submitResponse"
            >
              Gönder
            </v-btn>
          </template>
          <template v-else>
            <v-spacer />
            <v-btn
              color="success"
              depressed
              class="mr-2 text-capitalize"
              @click="responseAction = 'ONAY'"
            >
              Onayla
            </v-btn>
            <v-btn
              color="error"
              depressed
              class="text-capitalize"
              @click="responseAction = 'RET'"
            >
              Reddet
            </v-btn>
          </template>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from "vue-demi";

// Utils
import { useToast } from "vue-toastification/composition";

// Api
import {
  reconciliationPdf,
  setMutabakatApproval,
} from "@/api/reconciliation";

// Icons
import {
  mdiFileDocumentOutline,
  mdiCloseCircleOutline,
  mdiEyeOutline,
  mdiPaperclip,
} from "@mdi/js";

export default {
  props: ["pending"],
  setup(props, { emit }) {
    const dialog = ref(false);
    const toast = useToast();

    const closeForm = () => {
      dialog.value = false;
    };

    // Zaten onaylanmış/reddedilmiş bir mutabakat tekrar değiştirilemez
    const isAnswered = computed(() => props.pending.status != null && props.pending.status !== "");

    // pdf_url her zaman PDF olmayabilir (görsel de olabilir), uzantıya göre karar veriyoruz
    const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url || "");

    // Mutabakat PDF
    const pdfLoading = ref(false);
    const pdfError = ref(null);
    const pdfUrl = ref(null);
    const loadPdf = async () => {
      if (props.pending.pdf_url) {
        pdfUrl.value = props.pending.pdf_url;
        return;
      }

      pdfLoading.value = true;
      pdfError.value = null;
      pdfUrl.value = null;
      try {
        const response = await reconciliationPdf({
          mutabakat_no: props.pending.mutabakat_no,
        });
        if (response.error) {
          throw new Error(response.msg);
        }
        pdfUrl.value = response.response.result;
      } catch (e) {
        pdfError.value = e.message;
      } finally {
        pdfLoading.value = false;
      }
    };

    // Onay/Ret
    const responseAction = ref(null);
    const note = ref("");
    const file = ref(null);
    const approvalLoading = ref(false);

    const cancelResponse = () => {
      responseAction.value = null;
      note.value = "";
      file.value = null;
    };

    const isAllowedFileType = (f) => {
      const type = (f.type || "").toLowerCase();
      if (type.startsWith("image/")) return true;
      if (type === "application/pdf") return true;
      return false;
    };

    const submitResponse = async () => {
      const action = responseAction.value;
      const isReject = action === "RET";
      const trimmedNote = (note.value || "").trim();

      if (isReject && !trimmedNote) {
        toast.error("Ret nedeninizi yazınız", { position: "bottom-center" });
        return;
      }
      if (trimmedNote.length > 255) {
        toast.error("Açıklama en fazla 255 karakter olabilir", { position: "bottom-center" });
        return;
      }
      if (file.value && !isAllowedFileType(file.value)) {
        toast.error("Yalnızca görsel veya PDF dosyası ekleyebilirsiniz", { position: "bottom-center" });
        return;
      }

      approvalLoading.value = true;
      try {
        const response = await setMutabakatApproval({
          mutabakat_no: props.pending.mutabakat_no,
          action,
          note: trimmedNote,
          file: file.value,
        });

        if (response.error) {
          throw new Error(response.msg);
        }

        toast.success(
          isReject ? "Mutabakat reddedildi" : "Mutabakat onaylandı",
          { position: "bottom-center" }
        );

        // Liste API'den yenilenene kadar butonların tekrar tıklanabilir
        // kalmaması için durumu anında lokalde de işaretliyoruz.
        props.pending.status = isReject ? 4 : 3;

        closeForm();
        emit("responded", { pending: props.pending, isReject });
      } catch (e) {
        toast.error(e.message, { position: "bottom-center" });
        closeForm();
      } finally {
        approvalLoading.value = false;
      }
    };

    watch(dialog, (val) => {
      if (val) {
        loadPdf();
      } else {
        cancelResponse();
      }
    });

    return {
      dialog,
      closeForm,
      isAnswered,
      isImageUrl,

      pdfLoading,
      pdfError,
      pdfUrl,

      responseAction,
      note,
      file,
      approvalLoading,
      cancelResponse,
      submitResponse,

      icons: {
        mdiFileDocumentOutline,
        mdiCloseCircleOutline,
        mdiEyeOutline,
        mdiPaperclip,
      },
    };
  },
};
</script>
